import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { v4 as uuid } from "uuid";

export interface SaveImageConfig {
  image: Blob;
  existingFilename?: string;
  newFilename?: string;
  isPublic: boolean;
  folder?: string;
  mimeTypeParam?: string;
}

export const useImageStoreRemote = () => {
  const { userID, client: supabase } = useAuthContext();

  const saveImage = async (config: SaveImageConfig): Promise<string> => {
    if (!userID)
      throw new Error("useImageRemoteStore 17: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");

    const id = uuid();
    const mimeType = config.mimeTypeParam || config.image.type || "image/png";
    const extension = mimeType === "image/jpeg" ? "jpg" : "png";

    const pathPrefix = config.isPublic
      ? config.folder
        ? `${config.folder}/`
        : ""
      : config.folder
      ? `images/${userID}/${config.folder}/`
      : `images/${userID}/`;

    const existingFilePath = `${pathPrefix}${config.existingFilename}.${extension}`;
    const newFilePath = `${pathPrefix}${config.newFilename ?? id}.${extension}`;
    const bucket = config.isPublic ? "public-images" : "user-images";

    // 🔥 Force removal first
    await supabase.storage.from(bucket).remove([existingFilePath]);

    const { error } = await supabase.storage
      .from(bucket)
      .upload(newFilePath, config.image, {
        upsert: true, // still nice for safety, even though we delete
        contentType: mimeType,
      });

    if (error) throw error;

    console.log(`Uploaded image to: ${bucket}/${newFilePath}`);

    return id;
  };

  const getImageUrl = async (
    id: string,
    isPublic: boolean,
    folder?: string
  ): Promise<string | null> => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const filePath = isPublic
      ? folder
        ? `${folder}/${id}.png`
        : `${id}.png`
      : folder
      ? `images/${userID}/${folder}/${id}.png`
      : `images/${userID}/${id}.png`;

    if (isPublic) {
      const { data } = supabase.storage
        .from("public-images")
        .getPublicUrl(filePath);
      const url = data?.publicUrl || null;
      return url;
    }

    if (!userID)
      throw new Error("useImageRemoteStore 62: User not authenticated");

    const { data, error } = await supabase.storage
      .from("user-images")
      .createSignedUrl(filePath, 60 * 60); // 1 hour

    if (error || !data) return null;
    return data.signedUrl;
  };

  const getRenderedResultImageURL = async (projectID: string) => {
    const imageURL = await getImageUrl("result", false, `renders/${projectID}`);
    //Now I want to call supabase to get
    if (!imageURL) {
      console.error("Image URL is undefined");
      return;
    }
    return imageURL;
  };

  const duplicatePublicImage = async (id: string) => {
    if (!supabase) throw new Error("Supabase client not initialized");

    const newID = uuid();

    const { data, error } = await supabase.storage
      .from("public-images")
      .copy(`${id}.png`, `${newID}.png`);

    if (error) {
      console.error("Error duplicating image:", error);
      return null;
    }

    return newID;
  };

  return {
    saveImage,
    getImageUrl,
    duplicatePublicImage,
    getRenderedResultImageURL,
  };
};
